import { useState, useEffect } from 'react';
import { InsuranceBill, InsuranceBillsResponse } from '../pages/hospital/Billing/types';
import { getInsuranceBillsService } from '../services/billing';

interface UseInsuranceBillsProps {
    investigationUuid: string;
    filters?: {
        organizationId?: number;
        periodStart?: Date;
        periodEnd?: Date;
        status?: number;
    };
    enabled?: boolean;
}

interface UseInsuranceBillsResult {
    bills: InsuranceBill[];
    loading: boolean;
    error: string | null;
    refetch: () => void;
}

export function useInsuranceBills({
    investigationUuid,
    filters,
    enabled = true
}: UseInsuranceBillsProps): UseInsuranceBillsResult {
    const [bills, setBills] = useState<InsuranceBill[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchBills = async () => {
        if (!enabled || !investigationUuid) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            
            const response: InsuranceBillsResponse = await getInsuranceBillsService(
                investigationUuid,
                filters
            );
            
            if (response.status === 200) {
                setBills(response.bills || []);
            } else {
                setError(response.errorMessage || 'Failed to fetch insurance bills');
                setBills([]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch insurance bills');
            setBills([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBills();
    }, [investigationUuid, JSON.stringify(filters), enabled]);

    return {
        bills,
        loading,
        error,
        refetch: fetchBills
    };
}

